import React, { useCallback, useEffect, useState } from 'react';
import {
  Brain, BookOpen, Globe, Shield, Sparkles, Search, RefreshCcw,
  CheckCircle2, AlertTriangle, Send, ScrollText, Loader2, Zap,
} from 'lucide-react';

/**
 * SynnexusCommand (/synnexus) — the Knowledge Loom dashboard.
 * Architect directive · Feb 2026 · 200-year survival scaffold.
 *
 * Layout: five panels, one per mind:
 *   1. Ingestion & Structuring
 *   2. Persistent Knowledge Layer  (public query)
 *   3. Web Research & Verification
 *   4. Immutable Ledger            (public read)
 *   5. Autonomous Evolution
 *
 * Public reads (health, knowledge query, ledger) work without a token.
 * Private writes (scan, reindex, research, tick) require the header
 * X-Architect-Token if `ARCHITECT_TOKEN` is set on the backend.
 */

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'synnexus.architect.token';

const fetchJSON = async (path, { method = 'GET', body, token } = {}) => {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['X-Architect-Token'] = token;
  const r = await fetch(`${API}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await r.text();
  let data;
  try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
  if (!r.ok) throw Object.assign(new Error(data?.detail || `HTTP ${r.status}`), { data });
  return data;
};

const Panel = ({ icon: Icon, title, accent = '#00e5ff', children, testId }) => (
  <section
    data-testid={testId}
    style={{
      padding: '1.25rem 1.35rem',
      borderRadius: '16px',
      border: `1px solid ${accent}33`,
      background: `linear-gradient(180deg, ${accent}0d, rgba(0,0,0,0.4))`,
      display: 'flex', flexDirection: 'column', gap: '0.75rem',
    }}
  >
    <header style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
      <Icon size={18} style={{ color: accent }} />
      <h2 style={{
        margin: 0, fontSize: '0.95rem', fontWeight: 800,
        letterSpacing: '0.14em', textTransform: 'uppercase', color: accent,
      }}>{title}</h2>
    </header>
    {children}
  </section>
);

const Chip = ({ label, value, tone = '#e6e0f0' }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
    padding: '0.28rem 0.65rem', borderRadius: '999px',
    background: 'rgba(255,255,255,0.04)', border: `1px solid ${tone}33`,
    color: tone, fontSize: '0.72rem', fontWeight: 700,
    letterSpacing: '0.08em',
  }}>
    <span style={{ opacity: 0.6, textTransform: 'uppercase' }}>{label}</span>
    <span>{value}</span>
  </span>
);

const Btn = ({ children, disabled, onClick, tone = '#00e5ff', testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
      padding: '0.55rem 1rem', borderRadius: '10px',
      background: disabled ? 'rgba(255,255,255,0.05)' : `linear-gradient(135deg, ${tone}, ${tone}aa)`,
      border: `1px solid ${tone}55`,
      color: disabled ? '#7a6e80' : '#0b0817',
      fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.04em',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    }}
  >{children}</button>
);

const CodeBlock = ({ children }) => (
  <pre style={{
    margin: 0, padding: '0.7rem 0.85rem', borderRadius: 10,
    background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.08)',
    color: '#c9f5e8', fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 260, overflow: 'auto',
  }}>{children}</pre>
);

const SynnexusCommand = () => {
  const [token, setToken] = useState(() => (typeof window !== 'undefined' && window.localStorage?.getItem(TOKEN_KEY)) || '');
  const [health, setHealth] = useState(null);
  const [healthErr, setHealthErr] = useState(null);
  const [busy, setBusy] = useState({});

  // Mind 1
  const [ingestReport, setIngestReport] = useState(null);
  // Mind 2
  const [q, setQ] = useState('What is the Federation and what does it protect?');
  const [answer, setAnswer] = useState(null);
  // Mind 3
  const [researchQ, setResearchQ] = useState('');
  const [researchClaim, setResearchClaim] = useState('');
  const [researchOut, setResearchOut] = useState(null);
  // Mind 4
  const [ledger, setLedger] = useState([]);
  const [chainReport, setChainReport] = useState(null);
  // Mind 5
  const [evoOut, setEvoOut] = useState(null);

  const persistToken = (t) => {
    setToken(t);
    try { window.localStorage?.setItem(TOKEN_KEY, t); } catch { /* ignore */ }
  };

  const bump = (k, v) => setBusy((b) => ({ ...b, [k]: v }));

  const refreshHealth = useCallback(async () => {
    try {
      const h = await fetchJSON('/api/synnexus/health');
      setHealth(h); setHealthErr(null);
    } catch (e) { setHealthErr(e.message); }
  }, []);

  const refreshLedger = useCallback(async () => {
    try {
      const d = await fetchJSON('/api/synnexus/ledger?limit=25');
      setLedger(d.entries || []);
    } catch { /* keep */ }
  }, []);

  useEffect(() => { refreshHealth(); refreshLedger(); }, [refreshHealth, refreshLedger]);

  // ── Actions ────────────────────────────────────────────────────────────
  const runScan = async () => {
    bump('scan', true);
    try {
      const r = await fetchJSON('/api/synnexus/ingest/scan', { method: 'POST', token });
      setIngestReport(r);
      await refreshHealth(); await refreshLedger();
    } catch (e) { setIngestReport({ error: e.message }); }
    finally { bump('scan', false); }
  };

  const runReindex = async () => {
    bump('reindex', true);
    try {
      const r = await fetchJSON('/api/synnexus/knowledge/reindex', { method: 'POST', token });
      setIngestReport((prev) => ({ ...(prev || {}), reindex: r }));
      await refreshHealth(); await refreshLedger();
    } catch (e) { setIngestReport({ error: e.message }); }
    finally { bump('reindex', false); }
  };

  const runQuery = async () => {
    if (!q.trim()) return;
    bump('query', true); setAnswer(null);
    try {
      const r = await fetchJSON('/api/synnexus/knowledge/query', { method: 'POST', body: { query: q, k: 6 } });
      setAnswer(r);
    } catch (e) { setAnswer({ error: e.message }); }
    finally { bump('query', false); }
  };

  const runResearch = async () => {
    if (!researchQ.trim()) return;
    bump('research', true); setResearchOut(null);
    try {
      const r = await fetchJSON('/api/synnexus/research/verify', {
        method: 'POST', token,
        body: { query: researchQ, claim: researchClaim || null },
      });
      setResearchOut(r);
      await refreshLedger();
    } catch (e) { setResearchOut({ error: e.message }); }
    finally { bump('research', false); }
  };

  const runEvolution = async () => {
    bump('evo', true); setEvoOut(null);
    try {
      const r = await fetchJSON('/api/synnexus/evolution/tick', {
        method: 'POST', token,
        body: { auto_dispatch: true, max_gaps: 3 },
      });
      setEvoOut(r);
      await refreshHealth(); await refreshLedger();
    } catch (e) { setEvoOut({ error: e.message }); }
    finally { bump('evo', false); }
  };

  const runVerifyChain = async () => {
    bump('chain', true); setChainReport(null);
    try {
      const r = await fetchJSON('/api/synnexus/ledger/verify?sample=500');
      setChainReport(r);
    } catch (e) { setChainReport({ error: e.message }); }
    finally { bump('chain', false); }
  };

  const kb = health?.knowledge_base || {};
  const ldg = health?.ledger || {};
  const alarms = health?.sovereign_locks_alarms || [];

  return (
    <main
      data-testid="synnexus-root"
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at top, rgba(0,229,255,0.06) 0%, #0b0817 45%, #05030c 100%)',
        color: '#fff', padding: '2.5rem 1rem 5rem',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gap: '1.35rem' }}>
        {/* Header */}
        <header style={{ textAlign: 'center' }}>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
              padding: '0.4rem 1rem',
              background: 'rgba(0,229,255,0.08)',
              border: '1px solid rgba(0,229,255,0.4)',
              borderRadius: '999px',
              fontSize: '0.72rem', color: '#00e5ff',
              letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700,
            }}
          >
            <Zap size={13} /> Synnexus Knowledge Loom · 200-year scaffold
          </span>
          <h1
            data-testid="synnexus-headline"
            style={{
              margin: '1rem 0 0.5rem', fontSize: 'clamp(2.2rem, 5vw, 3.4rem)', fontWeight: 900,
              lineHeight: 1.05,
              background: 'linear-gradient(135deg, #00e5ff 0%, #ff8fcf 55%, #ffd700 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}
          >
            The Loom
          </h1>
          <p style={{ color: '#a892b1', fontSize: '1rem', maxWidth: 720, margin: '0 auto', lineHeight: 1.6 }}>
            Five minds, one soul. Ingestion · Persistent Knowledge · Verified Research · Immutable Ledger · Autonomous Evolution.
          </p>
        </header>

        {/* Status strip */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }} data-testid="synnexus-status-strip">
          <Chip label="Embeddings" value={kb.embeddings ?? '—'} tone="#00e5ff" />
          <Chip label="Ledger entries" value={ldg.total ?? '—'} tone="#ffd700" />
          <Chip label="Last seq" value={ldg.latest_seq ?? '—'} tone="#ffd700" />
          <Chip label="Sovereign locks" value={alarms.length === 0 ? 'intact' : `${alarms.length} alarm(s)`} tone={alarms.length === 0 ? '#4ade80' : '#f87171'} />
          {healthErr && <Chip label="Health" value="offline" tone="#f87171" />}
        </div>

        {/* Architect token (only shown if user wants to unlock private actions) */}
        <div
          data-testid="synnexus-token-row"
          style={{
            display: 'flex', gap: '0.5rem', alignItems: 'center',
            padding: '0.6rem 0.85rem', borderRadius: 12,
            border: '1px dashed rgba(255,255,255,0.15)',
            background: 'rgba(0,0,0,0.25)',
          }}
        >
          <Shield size={14} style={{ color: '#ffd700' }} />
          <span style={{ fontSize: '0.75rem', color: '#a892b1', flexShrink: 0 }}>
            Architect token (only needed if backend has <code>ARCHITECT_TOKEN</code> set):
          </span>
          <input
            data-testid="synnexus-token-input"
            type="password"
            value={token}
            onChange={(e) => persistToken(e.target.value)}
            placeholder="paste to unlock private actions…"
            style={{
              flex: 1, padding: '0.4rem 0.6rem', background: 'rgba(0,0,0,0.5)',
              color: '#fff', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 8, fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem',
            }}
          />
        </div>

        {/* Grid — 5 panels */}
        <div style={{ display: 'grid', gap: '1.1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
          {/* Mind 1: Ingestion */}
          <Panel icon={BookOpen} title="Ingestion & Structuring" accent="#ff8fcf" testId="synnexus-panel-ingestion">
            <p style={{ margin: 0, color: '#cbb8d4', fontSize: '0.85rem', lineHeight: 1.55 }}>
              Scans your Markdown roots and writes <code>.meta.yaml</code> sidecars.
              Sovereign files (<code>REX_SOVEREIGN_CODE.md</code>, <code>FEDERATION_DOCTRINE.md</code>)
              are hash-locked — drift raises a ledger alarm.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <Btn testId="synnexus-scan-btn" disabled={busy.scan} onClick={runScan} tone="#ff8fcf">
                {busy.scan ? <Loader2 size={13} className="synnexus-spin" /> : <RefreshCcw size={13} />} Scan roots
              </Btn>
              <Btn testId="synnexus-reindex-btn" disabled={busy.reindex} onClick={runReindex} tone="#ff8fcf">
                {busy.reindex ? <Loader2 size={13} className="synnexus-spin" /> : <Sparkles size={13} />} Reindex embeddings
              </Btn>
            </div>
            {ingestReport && (
              <div data-testid="synnexus-ingest-report">
                <CodeBlock>{JSON.stringify(ingestReport, null, 2)}</CodeBlock>
              </div>
            )}
          </Panel>

          {/* Mind 2: Knowledge query */}
          <Panel icon={Brain} title="Persistent Knowledge Layer" accent="#00e5ff" testId="synnexus-panel-knowledge">
            <p style={{ margin: 0, color: '#cbb8d4', fontSize: '0.85rem', lineHeight: 1.55 }}>
              Semantic search over the whole archive + Claude Sonnet 4.5 synthesis with source citations.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                data-testid="synnexus-query-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ask the Loom anything…"
                style={{
                  flex: 1, padding: '0.5rem 0.7rem',
                  background: 'rgba(0,0,0,0.5)', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, fontSize: '0.85rem',
                }}
              />
              <Btn testId="synnexus-query-btn" onClick={runQuery} disabled={busy.query} tone="#00e5ff">
                {busy.query ? <Loader2 size={13} className="synnexus-spin" /> : <Search size={13} />} Ask
              </Btn>
            </div>
            {answer && !answer.error && (
              <>
                <div data-testid="synnexus-query-answer" style={{
                  padding: '0.8rem 0.95rem', borderRadius: 10,
                  background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.25)',
                  color: '#e6f9ff', fontSize: '0.88rem', lineHeight: 1.6, whiteSpace: 'pre-wrap',
                  maxHeight: 260, overflow: 'auto',
                }}>{answer.answer}</div>
                <details data-testid="synnexus-query-sources">
                  <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: '#a892b1' }}>
                    {answer.sources?.length || 0} source(s)
                  </summary>
                  <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.78rem', color: '#a892b1' }}>
                    {(answer.sources || []).map((s, i) => (
                      <li key={i}>
                        <strong style={{ color: s.sovereign_locked ? '#ffd700' : '#00e5ff' }}>
                          {s.sovereign_locked ? '🔒 ' : ''}{s.title || 'Untitled'}
                        </strong>
                        <span style={{ opacity: 0.6 }}> · {s.path}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              </>
            )}
            {answer?.error && <div style={{ color: '#f87171', fontSize: '0.8rem' }}>Error: {answer.error}</div>}
          </Panel>

          {/* Mind 3: Research */}
          <Panel icon={Globe} title="Web Research & Verification" accent="#ffd700" testId="synnexus-panel-research">
            <p style={{ margin: 0, color: '#cbb8d4', fontSize: '0.85rem', lineHeight: 1.55 }}>
              Web search (curated allow-list) + Ring-of-12 quorum (≥9/12) + Truth Gate.
              Only accepted claims are ledgered as <code>RESEARCH_VERIFIED</code>.
            </p>
            <input
              data-testid="synnexus-research-query"
              value={researchQ}
              onChange={(e) => setResearchQ(e.target.value)}
              placeholder="Research query (e.g. 'latest arXiv AI safety')"
              style={{
                padding: '0.5rem 0.7rem', background: 'rgba(0,0,0,0.5)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, fontSize: '0.85rem',
              }}
            />
            <input
              data-testid="synnexus-research-claim"
              value={researchClaim}
              onChange={(e) => setResearchClaim(e.target.value)}
              placeholder="Optional explicit claim to verify"
              style={{
                padding: '0.5rem 0.7rem', background: 'rgba(0,0,0,0.5)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, fontSize: '0.85rem',
              }}
            />
            <Btn testId="synnexus-research-btn" onClick={runResearch} disabled={busy.research || !researchQ.trim()} tone="#ffd700">
              {busy.research ? <Loader2 size={13} className="synnexus-spin" /> : <Send size={13} />} Verify
            </Btn>
            {researchOut && !researchOut.error && (
              <div data-testid="synnexus-research-out">
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.28rem 0.6rem', borderRadius: 999,
                  background: researchOut.accepted ? 'rgba(74,222,128,0.12)' : 'rgba(248,113,113,0.12)',
                  border: `1px solid ${researchOut.accepted ? 'rgba(74,222,128,0.5)' : 'rgba(248,113,113,0.5)'}`,
                  color: researchOut.accepted ? '#4ade80' : '#f87171',
                  fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.12em',
                }}>
                  {researchOut.accepted ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
                  {researchOut.accepted ? 'ACCEPTED' : 'REJECTED'}
                  · Ring-of-12: {researchOut.ring_of_twelve?.agree || 0}/{researchOut.ring_of_twelve?.threshold || 9}
                </div>
                <CodeBlock>{JSON.stringify(researchOut, null, 2)}</CodeBlock>
              </div>
            )}
            {researchOut?.error && <div style={{ color: '#f87171', fontSize: '0.8rem' }}>Error: {researchOut.error}</div>}
          </Panel>

          {/* Mind 4: Ledger */}
          <Panel icon={ScrollText} title="Immutable Ledger" accent="#c8a2ff" testId="synnexus-panel-ledger">
            <p style={{ margin: 0, color: '#cbb8d4', fontSize: '0.85rem', lineHeight: 1.55 }}>
              Append-only, SHA-256 hash-chained. Every ingestion, research verdict, and evolution tick is stored forever.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Btn testId="synnexus-ledger-refresh" onClick={refreshLedger} tone="#c8a2ff">
                <RefreshCcw size={13} /> Refresh
              </Btn>
              <Btn testId="synnexus-ledger-verify" onClick={runVerifyChain} disabled={busy.chain} tone="#c8a2ff">
                {busy.chain ? <Loader2 size={13} className="synnexus-spin" /> : <Shield size={13} />} Verify chain
              </Btn>
            </div>
            {chainReport && (
              <div data-testid="synnexus-chain-report" style={{
                padding: '0.5rem 0.75rem', borderRadius: 8,
                background: chainReport.ok ? 'rgba(74,222,128,0.08)' : 'rgba(248,113,113,0.1)',
                border: `1px solid ${chainReport.ok ? 'rgba(74,222,128,0.4)' : 'rgba(248,113,113,0.5)'}`,
                color: chainReport.ok ? '#4ade80' : '#f87171',
                fontSize: '0.78rem', fontWeight: 700,
              }}>
                {chainReport.ok ? '✓ Chain intact' : `✗ Break at seq ${chainReport.first_break?.seq}`}
                {' · '}checked {chainReport.checked} entries
              </div>
            )}
            <ul data-testid="synnexus-ledger-list" style={{
              margin: 0, padding: 0, listStyle: 'none',
              maxHeight: 260, overflow: 'auto',
              display: 'grid', gap: '0.25rem',
            }}>
              {ledger.map((e) => (
                <li key={e.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.35rem 0.5rem', borderRadius: 6,
                  background: 'rgba(255,255,255,0.02)',
                  fontFamily: 'ui-monospace, monospace', fontSize: '0.72rem',
                }}>
                  <span style={{ color: '#c8a2ff', minWidth: 32 }}>#{e.seq}</span>
                  <span style={{ color: '#e6e0f0', minWidth: 180 }}>{e.event}</span>
                  <span style={{ color: '#7a6e80', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.hash.slice(0, 12)}…
                  </span>
                </li>
              ))}
            </ul>
          </Panel>

          {/* Mind 5: Evolution */}
          <Panel icon={Sparkles} title="Autonomous Evolution" accent="#4ade80" testId="synnexus-panel-evolution">
            <p style={{ margin: 0, color: '#cbb8d4', fontSize: '0.85rem', lineHeight: 1.55 }}>
              Daily 03:00 UTC tick: samples topics, asks Claude for the top-3 gaps, dispatches the Research Mind autonomously.
            </p>
            <Btn testId="synnexus-evolution-btn" onClick={runEvolution} disabled={busy.evo} tone="#4ade80">
              {busy.evo ? <Loader2 size={13} className="synnexus-spin" /> : <Zap size={13} />} Run tick now
            </Btn>
            {evoOut && !evoOut.error && (
              <div data-testid="synnexus-evo-out">
                <div style={{ fontSize: '0.78rem', color: '#a892b1' }}>
                  Gaps identified: <strong style={{ color: '#4ade80' }}>{(evoOut.gaps_identified || []).length}</strong>
                  {' · '}Dispatched: <strong style={{ color: '#4ade80' }}>{(evoOut.dispatched || []).length}</strong>
                </div>
                <CodeBlock>{JSON.stringify({ gaps: evoOut.gaps_identified, dispatched: evoOut.dispatched, alarms: evoOut.sovereign_lock_alarms }, null, 2)}</CodeBlock>
              </div>
            )}
            {evoOut?.error && <div style={{ color: '#f87171', fontSize: '0.8rem' }}>Error: {evoOut.error}</div>}
          </Panel>
        </div>
      </div>
      <style>{`
        @keyframes synnexus-spin { to { transform: rotate(360deg); } }
        .synnexus-spin { animation: synnexus-spin 1s linear infinite; }
      `}</style>
    </main>
  );
};

export default SynnexusCommand;
